import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS } from '../../../styles/theme';
import type { AdminDialogProps } from '../types/adminTools.types';
import type { CreateOrganizationResponse } from '../../../types/api';

interface CreateOrgDialogProps extends AdminDialogProps {
  newOrgName: string;
  onNewOrgNameChange: (value: string) => void;
  result: CreateOrganizationResponse | null;
  error: string;
  isPending: boolean;
  onCreate: () => Promise<void>;
}

export const CreateOrgDialog: React.FC<CreateOrgDialogProps> = ({
  open,
  onClose,
  newOrgName,
  onNewOrgNameChange,
  result,
  error,
  isPending,
  onCreate,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('adminToolsPage.orgManagement.createOrgDialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('adminToolsPage.orgManagement.createOrgDialog.orgName')}
            value={newOrgName}
            onChange={(e) => onNewOrgNameChange(e.target.value)}
            margin="normal"
            placeholder={t('adminToolsPage.orgManagement.createOrgDialog.orgNamePlaceholder')}
            disabled={isPending}
          />

          {result && (
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {result.message}
              {result.success && result.organization && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {t('adminToolsPage.orgManagement.createOrgDialog.slug', {
                    slug: result.organization.slug,
                  })}
                </Typography>
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {result?.success ? t('adminToolsPage.close') : t('adminToolsPage.cancel')}
        </Button>
        {!result?.success && (
          <Button
            onClick={onCreate}
            variant="contained"
            disabled={isPending || !newOrgName.trim()}
            startIcon={isPending ? <CircularProgress size={16} /> : <Add />}
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: BRAND_COLORS.forestGreen.main,
                opacity: 0.8,
              },
            }}
          >
            {isPending
              ? t('adminToolsPage.orgManagement.createOrgDialog.creating')
              : t('adminToolsPage.orgManagement.createOrgDialog.createOrg')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
